"use client";

import { useQueryClient } from "@tanstack/react-query";

import type { AdminDashUser } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import { adminDashUserSchema } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import { useRequestMutation } from "../../../../request/hooks/useRequestMutation";
import type { UseRequestQueryProps } from "../../../../request/hooks/useRequestQuery";
import { useRequestQuery } from "../../../../request/hooks/useRequestQuery";
import { keyAdminDashUser } from "./keyAdminDashUser";

export type UseRequestAdminDashboardMemberProps = UseRequestQueryProps & {
  communityId: number;
  userId: number;
};

export const useRequestAdminDashboardMember = ({
  communityId,
  userId,
  ...props
}: UseRequestAdminDashboardMemberProps) => {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: keyAdminDashUser({ communityId, userId }),
    });

    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey[0] as string[];
        return (
          Array.isArray(queryKey) &&
          queryKey.length >= 2 &&
          queryKey[0] === "admin-dash-members" &&
          queryKey[1] === communityId.toString()
        );
      },
    });
  };

  return {
    query: useRequestQuery({
      ...props,
      queryFnRequestProps: () => ({
        schema: adminDashUserSchema,
        urlBackendPath: `/communities/${communityId}/admin/users/${userId}`,
      }),
      queryKey: keyAdminDashUser({ communityId, userId }),
    }),
    mutationPut: useRequestMutation<
      typeof adminDashUserSchema,
      Partial<AdminDashUser>
    >({
      mutationFnRequestProps: (values) => ({
        method: "PUT",
        urlBackendPath: `/communities/${communityId}/admin/users/${userId}`,
        body: JSON.stringify(values),
      }),
      onSuccess: invalidateQueries,
    }),
  };
};
